<template>
  <client-only>
    <div class="feed">
      <read-categories class="tabs" :tags="tags" :active-tab="currentTab.slug" @tab-changed="tabChanged"></read-categories>
      <div class="read-content" v-if="read">
        <div class="flex justify-center items-center head" :style="`background: ${read.color ? read.color : '#fff'};
          color: ${read.titleColor ? read.titleColor : '#000'};`">
          <div class="w-1/2 h-full">
            <img :src="read.coverImage" alt="Item Image" class="cover-image">
          </div>
          <div class="w-1/2 h-full">
            <div class="info flex flex-col justify-center items-start h-full">
              <div class="title">{{ read.title }}</div>
              <div class="tags">{{ read.tag.title || '' }} {{ read.author ? 'by' : '' }}
                {{ read.author || '' }}
              </div>
            </div>
          </div>
          <div class="actions">
            <RoundedIcon ref="roundedIcon" :class="[isReadInAnyGallery(read) ? 'selected' : '']"
              src="/icons/plus_black.png" @click="addToHighlight(user.allRemarks, read)" />
          </div>
        </div>
        <div class="contents-wrapper">
          <div class="content-list">
            <div v-for="(content, index) in read.contents" :key="index" class="content-block">
              <ReadContentDisplay :block="content" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="explore-reads">
      <div class=" modal-content">
        <div class="flex items-center justify-between">
          <div class="explore-title">Discover latest news</div>
          <div class="flex-1 flex justify-end">
            <div class="create-button" @click="router.push('/reads')">Discover more</div>
          </div>
        </div>
        <div class="columns">
          <div class="column" v-for="(column, index) in items" :key="index">
            <ReadGridItem :active="false" v-for="item in column" :key="item._id" :read="item" />
          </div>
        </div>
      </div>
    </div>
  </client-only>

</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useReadStore } from "~/composables/useReadStore";
import { useTagStore } from "~/composables/useTagStore";
import { useUserStore } from "~/composables/useUserStore";
import { useRoute, useRouter } from "vue-router";
import ReadCategories from "~/components/reads/ReadCategories.vue";
import ReadContentDisplay from "~/components/reads/ReadContentDisplay.vue";

const { user, fetchUser } = useUserStore();
const { galleries, loadUserGalleries, patchGallery, createGallery, deleteGallery } = useWorkStore();
const { tags, loadTags } = useTagStore();

import ReadGridItem from "~/components/reads/ReadGridItem.vue";
import Popover from "~/components/Popover.vue";
import RoundedIcon from "~/components/RoundedIcon.vue";
import BoardItem from "~/components/works/BoardItem.vue";
import { useWorkStore } from "~/composables/useWorkStore";

const route = useRoute();
const router = useRouter();
const { loadReads, reads } = useReadStore();

const isPopoverOpen = ref(false);
const isPopoverDisabled = ref(false);
const currentTab = ref({ tag: '', slug: 'all' });

const tabChanged = async (tag: string, slug: string) => {
  console.log('tabChanged called with:', { tag, slug });
  
  // Set current tab information
  currentTab.value = { tag, slug };
  
  // Navigate to the appropriate route
  if (slug !== 'all') {
    router.push(`/reads/?tab=${slug}`);
  } else {
    router.push('/reads');
  }
}

definePageMeta({
  middleware: 'auth',
  layout: 'read-layout',
});

// Keep the useFetch for server-side rendering
const { data: read, error: readError } = await useFetch<any>(`https://api.artfield.app/api/reads/slug/${route.params.id}`, {
  key: route.params.id as string
});

// Add error handling
watch(readError, (newError) => {
  if (newError) {
    console.error('[DEBUG] Read fetch error:', newError);
  }
});

// SEO metadata precomputed values
const seoData = computed(() => {
  const defaultTitle = 'ARTFIELD';
  const defaultDescription = 'Curated space for creative minds.';
  const defaultImage = 'https://artfield.app/favicon.png';

  if (!read.value) return { title: defaultTitle, description: defaultDescription, image: defaultImage };

  const title = read.value.title ? `${read.value.title} | ARTFIELD` : defaultTitle;
  const description = read.value.description || defaultDescription;
  const image = read.value.coverImage || defaultImage;

  return { title, description, image };
});

// Set head metadata with precomputed values
useHead(() => ({
  title: seoData.value.title,
  meta: [
    { name: 'description', content: seoData.value.description },
    // Open Graph
    { property: 'og:title', content: seoData.value.title },
    { property: 'og:description', content: seoData.value.description },
    { property: 'og:image', content: seoData.value.image },
    { property: 'og:type', content: 'article' }, // Changed to 'article' type
    // Twitter Card
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: seoData.value.title },
    { name: 'twitter:description', content: seoData.value.description },
    { name: 'twitter:image', content: seoData.value.image }
  ]
}));

const updated = async () => {
  if (process.client) {
    await fetchUser();
    await loadUserGalleries();
  }
}

const addToHighlight = async (gallery: any, read: any) => {
  const galleryReadsIds = gallery.reads.map((r: any) => r ? r.read._id || r : '');
  console.log('gallery', galleryReadsIds);
  if (galleryReadsIds.includes(read._id)) {
    const indexOf = galleryReadsIds.indexOf(read._id);
    gallery.reads.splice(indexOf, 1);
  } else {

    const readItem = {
      read: read._id
    }
    gallery.reads.push(readItem);
  }
  await saveGallery(gallery);
}

const saveGallery = async (gallery: any) => {
  const save = await patchGallery(gallery, gallery._id);
  console.log('saved', save);
  await updated();
};
const isReadInGallery = (gallery: any, read: any) => {
  return gallery.reads.some((galRead: any) => {
    return galRead.read._id === read._id
  });
}

const isReadInAnyGallery = (read: any) => {
  const gals = galleries.value;
  const allRemarks = user.value.allRemarks;
  // Check if the work exists in the allRemarks gallery
  const isInAllRemarks = isReadInGallery(allRemarks, read);

  if (isInAllRemarks) {
    return true;
  }

  // Check if the work exists in any of the other galleries
  return gals.some((gallery: any) => isReadInGallery(gallery, read));
}

const items = computed(() => {
  const columns = [[] as any, [] as any, [] as any] as any; // Create three empty arrays for the columns
  reads.value.forEach((read, index) => {
    console.log('index', read, index)
    columns[index % 3].push(read); // Distribute works into columns
  });
  return columns;
});

// Update onMounted to handle client-side refreshing if needed
onMounted(async () => {
  if (process.client) {
    try {
      // Load tags first
      await loadTags();
      
      // Fetch user data and related reads
      await fetchUser();
      await loadReads({ status: 'published', skip: 0, limit: 3 });
      console.log('read', read.value);
    } catch (error) {
      console.error('Error initializing read page:', error);
    }
  }
});

</script>

<style scoped>
.explore-title {
  font-size: 32px;
  line-height: 42px;
  padding-left: 18px;
  font-weight: 300;
}

.explore-reads .columns {
  padding-top: 10px;
}

.cover-image {
  width: 100%;
  height: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 10px;

  border-right: .5px solid #b8b8b8;
}

body {
  background: #FAFAFA;
}

.feed {
  padding: 0 80px 0 80px;

}

.tabs {
  padding-top: 10px;
  padding-bottom: 40px;
}

.read-content {
  color: black;
  position: relative;
  max-width: 1280px;
  display: flex;
  flex-direction: column;
  gap: 36px;
  margin-bottom: 0;
}

.head {
  height: 640px;
  border-radius: 10px;
  border: .5px solid #b8b8b8;
  position: relative;
}

.info {
  padding: 36px;
}

.info .title {
  font-size: 64px;
  line-height: 74px;
}

.info .tags {
  font-size: 20px;
}

.actions {
  position: absolute;
  top: 18px;
  right: 18px;
}

.contents-wrapper .content-list {
  display: flex;
  flex-direction: column;
  max-width: 960px;
}

.explore-reads {
  height: 450px;
  width: 90vw;
  position: relative;
  margin: 30px auto 0 auto;
}

.contents-wrapper {
  border-radius: 10px;
  border: .5px solid #b8b8b8;
  width: 100%;
  padding: 60px 0;
  display: flex;
  justify-content: center;
}

.columns {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  position: relative;
}

.modal-content {
  height: 450px;
  position: absolute;
  margin: 0;
  width: 100%;
  overflow: hidden;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 20px;
}

.column {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}
</style>
